/**
 * Sketch-mode cursor.
 *
 * Thin wrapper around the shared BrushCursor — resolves sketch-specific
 * color (active sketch color / red for erase) and radius each frame.
 */

import { useCallback } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import { isVisibleInWorkspace } from "../app/visibilityPolicy.ts";
import {
  BrushCursor,
  type BrushCursorState,
  type BrushCursorStyle,
} from "./BrushCursor.tsx";

const COLOR_ERASE = new THREE.Color(0xd94a4a);
const _parsedColor = new THREE.Color();

export function SketchCursor({
  cursorRef,
}: {
  cursorRef: React.MutableRefObject<BrushCursorState>;
}) {
  const getStyle = useCallback((): BrushCursorStyle | null => {
    const state = useStore.getState();
    const pm = state.sketchMode;
    if (!pm) return null;

    // Sketch mode no longer targets a specific object — the cursor is
    // always permitted in sketch workspace.
    if (
      !isVisibleInWorkspace({
        workspaceMode: state.workspaceMode,
        viewportVisible: true,
        renderVisible: true,
      })
    ) {
      cursorRef.current.visible = false;
      return null;
    }

    const isErase = pm.tool === "erase";
    const color = isErase
      ? COLOR_ERASE
      : _parsedColor.set(parseInt(pm.color.replace("#", ""), 16));

    return {
      color,
      radius: pm.thickness,
    };
  }, [cursorRef]);

  return <BrushCursor cursorRef={cursorRef} getStyle={getStyle} />;
}
